import { CustomWidgetDbEntity, CustomWidgetVariant } from '@kemu-io/kemu-types';
import { ActionReducerMapBuilder, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { buildWidgetRequestBody, decodeServerErrorInfo } from './utils';
import widgetCache from './widgetCache';
import { WidgetCollectionState } from '@src/app/reducers/widget/widgetSlice';
import widgetApi from '@src/api/widget/widgetApi';
import { AsyncRequestStatus } from '@src/types/core_t';
import { editCustomWidgetState } from '@src/app/recipe/utils';
import { getGlobalIntl } from '@src/assets/i18n/globalIntl';
import { showGlobalNotification, showPremiumUpgradeModal } from '@src/features/interface/interfaceSlice';
import { SUBSCRIPTION_LIMIT_REACHED } from '@common/constants';

/**
 * Saves the given widget in the database and uploads the contents to S3.
 * It also adds the widget to the local cache.
*/
export const saveWidgetAction = createAsyncThunk('/widgets/saveWidgetAction', async (
	args: {
		widgetIdInRecipe: string;
		recipeId: string;
		thingId: string;
		variant: CustomWidgetVariant;
	}, thunkAPI
): Promise<CustomWidgetDbEntity | null> => {

	try {
		const t = getGlobalIntl();
	
		thunkAPI.dispatch(showGlobalNotification({
			code: 'save_widget',
			message: t('Widget.AsyncIndicator.Progress', { action: t('CommonWords.Saving') }),
			showProgress: true,
			duration: 30,
			type: 'info',
		}));

		const {
			requestObject,
			// contentsBuf,
			contentsStr,
			widgetState
		} = buildWidgetRequestBody({
			widgetId: args.widgetIdInRecipe,
			thingId: args.thingId,
			recipeId: args.recipeId,
			translateFunction: t,
			variant: args.variant,
		});
	
		const response = await widgetApi.saveNewWidget({
			info: requestObject,
			rawContents: contentsStr,
		});
	
		// Store the widget locally
		await widgetCache.storeWidget(response.widget.id, response.widget.version, contentsStr);
	
		// Update the recipe reference
		editCustomWidgetState(args.recipeId, args.thingId, args.widgetIdInRecipe, {
			...widgetState,
			collectionInfo: {
				userId: response.widget.userId,
				version: response.widget.version,
				widgetId: response.widget.id
			}
		}, true);
	
		thunkAPI.dispatch(showGlobalNotification({
			code: 'save_widget',
			message: t('Widget.AsyncIndicator.Success', { action: t('CommonWords.Saved') }),
			type: 'success',
		}));

		return {
			...response.widget,
			contents: contentsStr
		};
	}catch (error) {
		const t = getGlobalIntl();
		console.error('Failed to save widget:', error);
		const { errorCode, message } = decodeServerErrorInfo(error);
		thunkAPI.dispatch(showGlobalNotification({
			code: 'save_widget',
			title: t('Widget.AsyncIndicator.Failure.Title', { action: t('CommonWords.Save') }),
			message: message,
			type: 'error',
		}));

		if(errorCode === SUBSCRIPTION_LIMIT_REACHED){
			thunkAPI.dispatch(showPremiumUpgradeModal(true));
		}

		return null;
	}
});


export const saveWidgetReducer = ((builder: ActionReducerMapBuilder<WidgetCollectionState>/*, thunk: any*/): void => {

	builder.addCase(saveWidgetAction.fulfilled, (state, action: PayloadAction<CustomWidgetDbEntity | null>) => {
		state.asyncIndicator.asyncStatus.status = AsyncRequestStatus.completed;
		
		if(!action.payload){
			return;
		}

		// const t = getGlobalIntl();
		state.selectedWidget = action.payload.id;

		// Add new widget to local collection
		state.customWidgets = [
			...state.customWidgets,
			{
				dbId: action.payload.id,
				description: action.payload.htmlDescription,
				isDefault: !!action.payload.isDefault,
				meta: action.payload.meta,
				name: action.payload.name,
				protocolVersion: action.payload.protocolVersion,
				version: action.payload.version,
				color: action.payload.color,
				icon: action.payload.icon,
				contents: action.payload.contents,
				author: action.payload.author,
				variant: action.payload.variant,
			}
		];
	});
});

